import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '../../lib/utils';
import { useWebSocket } from '../../hooks/useWebSocket';
import { 
  Package, ShoppingCart,
  ExternalLink, Printer, Bell, Volume2, VolumeX,
  Clock, CheckCircle, ChefHat, Truck, XCircle, Ban, AlertCircle,
  MapPin, Edit2, RefreshCw, QrCode, DollarSign, Wifi, WifiOff
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending: { label: 'Pendente', color: 'bg-amber-100 text-amber-800', icon: Clock },
  confirmed: { label: 'Confirmado', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
  preparing: { label: 'Preparando', color: 'bg-purple-100 text-purple-800', icon: ChefHat },
  ready: { label: 'Pronto', color: 'bg-green-100 text-green-800', icon: Package },
  delivered: { label: 'Entregue', color: 'bg-gray-100 text-gray-800', icon: Truck },
  cancelled: { label: 'Cancelado', color: 'bg-red-100 text-red-800', icon: XCircle },
  rejected: { label: 'Rejeitado', color: 'bg-red-100 text-red-800', icon: Ban }
};

const REJECTION_REASONS = [
  "Produto indisponível",
  "Endereço fora da área de entrega",
  "Estabelecimento fechado",
  "Problemas técnicos",
  "Outro motivo"
];

function KanbanColumn({ status, orders, onUpdateStatus, onPrint, onReject, onEditDeliveryFee, onConfirmPix }) {
  const config = STATUS_CONFIG[status];
  const Icon = config.icon;
  
  const getNextStatus = (currentStatus) => {
    const flow = { pending: 'confirmed', confirmed: 'preparing', preparing: 'ready', ready: 'delivered' };
    return flow[currentStatus];
  };

  // Check if PIX payment is pending for an order
  const isPixPending = (order) => {
    return order.payment_method === 'pix' && !order.pix_confirmed;
  };

  return (
    <div className="kanban-column" data-testid={`kanban-column-${status}`}>
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-5 h-5 text-slate-600" />
        <h3 className="font-semibold text-slate-700">{config.label}</h3>
        <Badge variant="secondary" className="ml-auto">{orders.length}</Badge>
      </div>
      <div className="flex-1 overflow-y-auto space-y-3">
        {orders.map(order => (
          <div 
            key={order.id} 
            className={`kanban-card ${status} ${status === 'pending' ? 'animate-blink' : ''} ${isPixPending(order) ? 'ring-2 ring-amber-400' : ''}`}
            data-testid={`order-card-${order.id}`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-sm font-semibold">{order.order_number}</span>
              <span className={`status-badge ${status}`}>{config.label}</span>
            </div>
            
            {/* PIX Pending Badge */}
            {isPixPending(order) && (
              <div className="flex items-center gap-2 mb-2 px-2 py-1 bg-amber-100 border border-amber-300 rounded-md" data-testid={`pix-pending-badge-${order.id}`}>
                <QrCode className="w-4 h-4 text-amber-600" />
                <span className="text-xs font-medium text-amber-700">PIX - Aguardando confirmação</span>
              </div>
            )}

            {/* PIX Confirmed Badge */}
            {order.payment_method === 'pix' && order.pix_confirmed && (
              <div className="flex items-center gap-2 mb-2 px-2 py-1 bg-green-100 border border-green-300 rounded-md" data-testid={`pix-confirmed-badge-${order.id}`}>
                <DollarSign className="w-4 h-4 text-green-600" />
                <span className="text-xs font-medium text-green-700">PIX Confirmado</span>
              </div>
            )}
            
            <p className="font-medium text-slate-800">{order.customer_name}</p>
            <p className="text-sm text-slate-500">{order.customer_phone}</p>
            {order.customer_address && (
              <p className="text-xs text-slate-400 flex items-center gap-1 mt-1">
                <MapPin className="w-3 h-3" />
                {order.customer_address.length > 40 
                  ? order.customer_address.substring(0, 40) + '...' 
                  : order.customer_address}
              </p>
            )}
            <p className="text-xs text-slate-400 mt-1">{formatDate(order.created_at)}</p>
            
            <div className="mt-3 pt-3 border-t border-slate-100">
              <p className="text-xs text-slate-500 mb-1">{order.items.length} item(ns)</p>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-400">Subtotal: {formatCurrency(order.subtotal || 0)}</p>
                  <p className="text-xs text-slate-400 flex items-center gap-1">
                    Entrega: {formatCurrency(order.delivery_fee || 0)}
                    {(status === 'pending' || status === 'confirmed') && order.delivery_type === 'delivery' && (
                      <button 
                        onClick={() => onEditDeliveryFee(order)}
                        className="text-blue-500 hover:text-blue-700"
                        title="Ajustar taxa de entrega"
                        data-testid={`edit-delivery-fee-${order.id}`}
                      >
                        <Edit2 className="w-3 h-3" />
                      </button>
                    )}
                  </p>
                  <p className="text-xs text-slate-500">
                    Pagamento: <span className="font-medium">{order.payment_method === 'pix' ? 'PIX' : order.payment_method === 'card' ? 'Cartão' : 'Dinheiro'}</span>
                  </p>
                </div>
                <p className="font-semibold text-slate-800">{formatCurrency(order.total)}</p>
              </div>
            </div>
            
            <div className="flex gap-2 mt-3 flex-wrap">
              {/* PIX Confirm Button */}
              {isPixPending(order) && (
                <Button 
                  size="sm" 
                  className="flex-1 bg-green-500 hover:bg-green-600"
                  onClick={() => onConfirmPix(order)}
                  data-testid={`confirm-pix-${order.id}`}
                >
                  <DollarSign className="w-4 h-4 mr-1" />
                  Confirmar PIX
                </Button>
              )}
              
              {getNextStatus(status) && (
                <Button 
                  size="sm" 
                  className={`flex-1 ${isPixPending(order) ? 'bg-slate-600' : 'bg-slate-800'} hover:bg-slate-900`}
                  onClick={() => onUpdateStatus(order.id, getNextStatus(status))}
                  data-testid={`advance-order-${order.id}`}
                >
                  Avançar
                </Button>
              )}
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => onPrint(order)}
                data-testid={`print-order-${order.id}`}
              >
                <Printer className="w-4 h-4" />
              </Button>
              {status === 'pending' && (
                <Button 
                  size="sm" 
                  variant="outline"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => onReject(order)}
                  data-testid={`reject-order-${order.id}`}
                >
                  <Ban className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        ))}
        {orders.length === 0 && (
          <p className="text-center text-slate-400 text-sm py-8">Nenhum pedido</p>
        )}
      </div>
    </div>
  );
}

function OrderReceipt({ order, establishment }) {
  return (
    <div className="thermal-receipt">
      {/* Styles for 80mm thermal printer - ALL BOLD & LARGER FONTS */}
      <style>{`
        @media print {
          @page {
            size: 80mm auto;
            margin: 0;
          }
          body * {
            visibility: hidden;
          }
          .print-only, .print-only * {
            visibility: visible;
          }
          .print-only {
            position: absolute;
            left: 0;
            top: 0;
            width: 80mm;
          }
          .no-print {
            display: none !important;
          }
        }
        .thermal-receipt {
          width: 80mm;
          max-width: 80mm;
          font-family: 'Courier New', monospace;
          font-size: 16px;
          font-weight: bold;
          line-height: 1.5;
          padding: 3mm;
          box-sizing: border-box;
        }
        .thermal-receipt * {
          font-weight: bold !important;
          font-family: 'Courier New', monospace !important;
        }
        .thermal-receipt .header {
          text-align: center;
          border-bottom: 3px solid #000;
          padding-bottom: 10px;
          margin-bottom: 10px;
        }
        .thermal-receipt .header h1 {
          font-size: 22px;
          font-weight: bold !important;
          margin: 0 0 6px 0;
          text-transform: uppercase;
        }
        .thermal-receipt .header p {
          font-size: 14px;
          font-weight: bold !important;
          margin: 4px 0;
        }
        .thermal-receipt .order-info {
          border-bottom: 3px solid #000;
          padding-bottom: 10px;
          margin-bottom: 10px;
        }
        .thermal-receipt .order-info p {
          margin: 6px 0;
          font-size: 15px;
          font-weight: bold !important;
        }
        .thermal-receipt .order-info .order-number {
          font-size: 28px;
          font-weight: bold !important;
          text-align: center;
          margin: 12px 0;
          padding: 10px;
          border: 4px solid #000;
          background: #e0e0e0;
        }
        .thermal-receipt .items {
          margin-bottom: 10px;
          padding-bottom: 10px;
          border-bottom: 3px solid #000;
        }
        .thermal-receipt .items h2 {
          font-size: 18px;
          font-weight: bold !important;
          margin: 0 0 12px 0;
          text-transform: uppercase;
          text-align: center;
          padding: 6px;
          background: #000;
          color: #fff;
        }
        .thermal-receipt .item {
          margin-bottom: 14px;
          padding-bottom: 10px;
          border-bottom: 2px dashed #000;
        }
        .thermal-receipt .item:last-child {
          border-bottom: none;
        }
        .thermal-receipt .item-header {
          display: flex;
          justify-content: space-between;
          font-size: 17px;
          font-weight: bold !important;
        }
        .thermal-receipt .item-variation {
          font-size: 16px;
          font-weight: bold !important;
          margin-left: 8px;
          margin-top: 4px;
          color: #000;
        }
        .thermal-receipt .item-addon {
          font-size: 15px;
          font-weight: bold !important;
          margin-left: 8px;
          margin-top: 4px;
          padding: 2px 0;
        }
        .thermal-receipt .item-notes {
          font-size: 15px;
          font-weight: bold !important;
          margin-left: 8px;
          margin-top: 6px;
          padding: 6px 10px;
          background: #e8e8e8;
          border-left: 4px solid #000;
        }
        .thermal-receipt .totals {
          margin-top: 12px;
        }
        .thermal-receipt .totals .row {
          display: flex;
          justify-content: space-between;
          margin: 8px 0;
          font-size: 16px;
          font-weight: bold !important;
        }
        .thermal-receipt .totals .total-row {
          font-size: 22px;
          font-weight: bold !important;
          border-top: 4px solid #000;
          padding-top: 12px;
          margin-top: 12px;
        }
        .thermal-receipt .payment-method {
          text-align: center;
          margin: 14px 0;
          padding: 12px;
          background: #000;
          color: #fff;
          font-weight: bold !important;
          font-size: 18px;
          text-transform: uppercase;
        }
        .thermal-receipt .footer {
          text-align: center;
          font-size: 14px;
          font-weight: bold !important;
          margin-top: 16px;
          padding-top: 12px;
          border-top: 3px solid #000;
        }
      `}</style>
      
      {/* Header */}
      <div className="header">
        <h1>{establishment?.name}</h1>
        {establishment?.address && <p>{establishment?.address}</p>}
        {establishment?.phone && <p>Tel: {establishment?.phone}</p>}
      </div>
      
      {/* Order Info */}
      <div className="order-info">
        <div className="order-number">PEDIDO #{order.order_number}</div>
        <p><strong>Data:</strong> {formatDate(order.created_at)}</p>
        <p><strong>Cliente:</strong> {order.customer_name}</p>
        <p><strong>Telefone:</strong> {order.customer_phone}</p>
        {order.customer_address && <p><strong>Endereço:</strong> {order.customer_address}</p>}
        <p><strong>Tipo:</strong> {order.delivery_type === 'delivery' ? 'ENTREGA' : 'RETIRADA'}</p>
      </div>
      
      {/* Items */}
      <div className="items">
        <h2>─── ITENS DO PEDIDO ───</h2>
        {order.items.map((item, index) => (
          <div key={index} className="item">
            <div className="item-header">
              <span>{item.quantity}x {item.product_name}</span>
              <span>{formatCurrency(item.unit_price * item.quantity)}</span>
            </div>
            {/* Show variation */}
            {item.variation && (
              <div className="item-variation">
                ↳ {item.variation.name} ({formatCurrency(item.variation.price)})
              </div>
            )}
            {/* Show addons */}
            {item.addons?.map((addon, ai) => (
              <div key={ai} className="item-addon">
                + {addon.quantity}x {addon.name} {addon.price > 0 && formatCurrency(addon.price * addon.quantity)}
              </div>
            ))}
            {/* Show notes */}
            {item.notes && (
              <div className="item-notes">OBS: {item.notes}</div>
            )}
          </div>
        ))}
      </div>
      
      {/* Totals */}
      <div className="totals">
        <div className="row">
          <span>Subtotal:</span>
          <span>{formatCurrency(order.subtotal || 0)}</span>
        </div>
        {order.delivery_type === 'delivery' && (
          <div className="row">
            <span>Taxa de Entrega:</span>
            <span>{formatCurrency(order.delivery_fee || 0)}</span>
          </div>
        )}
        <div className="row total-row">
          <span>TOTAL:</span>
          <span>{formatCurrency(order.total)}</span>
        </div>
      </div>
      
      {/* Payment Method */}
      <div className="payment-method">
        PAGAMENTO: {order.payment_method === 'pix' ? 'PIX' : order.payment_method === 'card' ? 'CARTÃO' : 'DINHEIRO'}
        {order.payment_method === 'pix' && order.pix_confirmed && ' ✓ CONFIRMADO'}
      </div>
      
      {/* Order Notes */}
      {order.notes && (
        <div style={{ marginTop: '8px', padding: '8px', border: '1px dashed #000' }}>
          <strong>Observações:</strong> {order.notes}
        </div>
      )}
      
      {/* Footer */}
      <div className="footer">
        <p>Obrigado pela preferência!</p>
        <p>─────────────────────</p>
      </div>
    </div>
  );
}

export default function Pedidos() {
  const [establishment, setEstablishment] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectingOrder, setRejectingOrder] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [showDeliveryFeeDialog, setShowDeliveryFeeDialog] = useState(false);
  const [editingDeliveryFee, setEditingDeliveryFee] = useState(null);
  const [newDeliveryFee, setNewDeliveryFee] = useState('');
  const [showPixConfirmDialog, setShowPixConfirmDialog] = useState(false);
  const [confirmingPixOrder, setConfirmingPixOrder] = useState(null);
  const audioRef = useRef(null);
  const previousOrdersRef = useRef([]);
  const fetchOrdersRef = useRef(null);

  const fetchEstablishment = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/establishments`);
      if (response.data.length > 0) {
        setEstablishment(response.data[0]);
        setError(null);
      } else {
        setError('no_establishment');
      }
    } catch (err) {
      console.error('Error fetching establishment:', err);
      // Don't set error state on auth errors - let the interceptor handle it
      if (err.response?.status === 401 || err.response?.status === 403) {
        console.warn('[Pedidos] Authentication error during establishment fetch');
        return;
      }
      setError('fetch_error');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchOrders = useCallback(async () => {
    if (!establishment) return;
    try {
      const response = await axios.get(`${API}/establishments/${establishment.id}/orders`);
      const newOrders = response.data;
      
      // Check for new pending orders (only when polling, not from WebSocket)
      const prevPendingIds = previousOrdersRef.current
        .filter(o => o.status === 'pending')
        .map(o => o.id);
      const newPendingOrders = newOrders.filter(
        o => o.status === 'pending' && !prevPendingIds.includes(o.id)
      );
      
      // Only play sound from polling if we're not using WebSocket
      // WebSocket handler will play sound for new orders
      if (newPendingOrders.length > 0 && soundEnabled && audioRef.current && !fetchOrdersRef.current?.fromWebSocket) {
        audioRef.current.play().catch(console.error);
      }
      
      previousOrdersRef.current = newOrders;
      setOrders(newOrders);
    } catch (err) {
      console.error('Error fetching orders:', err);
      // Don't retry on authentication errors - the global interceptor will handle logout
      if (err.response?.status === 401 || err.response?.status === 403) {
        console.warn('[Pedidos] Authentication error - stopping order polling');
        return;
      }
    }
  }, [establishment, soundEnabled]);

  // Update ref when fetchOrders changes
  useEffect(() => {
    fetchOrdersRef.current = { fn: fetchOrders, fromWebSocket: false };
  }, [fetchOrders]);

  // WebSocket handler for real-time notifications
  const handleWebSocketMessage = useCallback((message) => {
    console.log('[Pedidos] WebSocket message:', message);
    
    if (message.type === 'new_order') {
      // New order received - play sound
      if (soundEnabled && audioRef.current) {
        audioRef.current.play().catch(console.error);
      }
      
      // Show toast notification
      const isPix = message.order?.is_pix;
      toast.success(
        `🔔 Novo Pedido! ${message.order?.order_number}${isPix ? ' (PIX)' : ''}`,
        {
          description: `${message.order?.customer_name} - ${formatCurrency(message.order?.total || 0)}`,
          duration: 5000
        }
      );
      
      // Refresh orders (mark as from WebSocket to avoid double sound)
      if (fetchOrdersRef.current) {
        fetchOrdersRef.current.fromWebSocket = true;
        fetchOrdersRef.current.fn();
        setTimeout(() => {
          if (fetchOrdersRef.current) fetchOrdersRef.current.fromWebSocket = false;
        }, 1000);
      }
    } else if (message.type === 'order_status_changed') {
      // Status changed - refresh to update UI
      if (fetchOrdersRef.current) fetchOrdersRef.current.fn();
    } else if (message.type === 'pix_confirmed') {
      // PIX confirmed - show toast and refresh
      toast.success(`✅ PIX Confirmado: ${message.order_number}`);
      if (fetchOrdersRef.current) fetchOrdersRef.current.fn();
    }
  }, [soundEnabled]);

  // WebSocket connection
  const { isConnected } = useWebSocket(
    establishment?.id,
    handleWebSocketMessage,
    !!establishment
  );

  useEffect(() => {
    fetchEstablishment();
  }, [fetchEstablishment]);

  useEffect(() => {
    if (establishment) {
      fetchOrders();
      // Reduce polling frequency when WebSocket is connected (backup only)
      const pollInterval = isConnected ? 30000 : 10000;
      const interval = setInterval(fetchOrders, pollInterval);
      return () => clearInterval(interval);
    }
  }, [establishment, fetchOrders, isConnected]);

  const updateOrderStatus = async (orderId, newStatus, reason = null) => {
    try {
      const data = { status: newStatus };
      if (reason) data.rejection_reason = reason;
      
      await axios.put(`${API}/establishments/${establishment.id}/orders/${orderId}/status`, data);
      toast.success(`Pedido ${newStatus === 'delivered' ? 'finalizado' : 'atualizado'}!`);
      fetchOrders();
    } catch (err) {
      toast.error('Erro ao atualizar pedido');
    }
  };

  const handlePrintOrder = (order) => {
    setSelectedOrder(order);
    setTimeout(() => {
      window.print();
    }, 100);
  };

  const handleOpenRejectDialog = (order) => {
    setRejectingOrder(order);
    setRejectionReason('');
    setCustomReason('');
    setShowRejectDialog(true);
  };

  const handleConfirmReject = async () => {
    if (!rejectingOrder) return;
    
    const reason = rejectionReason === 'Outro motivo' ? customReason : rejectionReason;
    
    if (!reason.trim()) {
      toast.error('Por favor, informe o motivo da rejeição');
      return;
    }
    
    await updateOrderStatus(rejectingOrder.id, 'rejected', reason);
    setShowRejectDialog(false);
    setRejectingOrder(null);
    setRejectionReason('');
    setCustomReason('');
  };

  const handleOpenDeliveryFeeDialog = (order) => {
    setEditingDeliveryFee(order);
    setNewDeliveryFee(order.delivery_fee?.toString() || '0');
    setShowDeliveryFeeDialog(true);
  };

  const handleUpdateDeliveryFee = async () => {
    if (!editingDeliveryFee) return;
    
    const feeValue = parseFloat(newDeliveryFee.replace(',', '.'));
    if (isNaN(feeValue) || feeValue < 0) {
      toast.error('Por favor, informe um valor válido');
      return;
    }
    
    try {
      await axios.put(
        `${API}/establishments/${establishment.id}/orders/${editingDeliveryFee.id}/delivery-fee`,
        { delivery_fee: feeValue }
      );
      toast.success('Taxa de entrega atualizada!');
      fetchOrders();
      setShowDeliveryFeeDialog(false);
      setEditingDeliveryFee(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao atualizar taxa de entrega');
    }
  };

  const handleOpenPixConfirmDialog = (order) => {
    setConfirmingPixOrder(order);
    setShowPixConfirmDialog(true);
  };

  const handleConfirmPixPayment = async () => {
    if (!confirmingPixOrder) return;
    
    try {
      await axios.put(
        `${API}/establishments/${establishment.id}/orders/${confirmingPixOrder.id}/confirm-pix`,
        { confirmed: true }
      );
      toast.success('Pagamento PIX confirmado!');
      fetchOrders();
      setShowPixConfirmDialog(false);
      setConfirmingPixOrder(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao confirmar pagamento');
    }
  };

  const getOrdersByStatus = (status) => {
    return orders.filter(o => o.status === status);
  };

  const pendingCount = getOrdersByStatus('pending').length;
  const pixPendingCount = orders.filter(o => o.payment_method === 'pix' && !o.pix_confirmed && o.status !== 'delivered' && o.status !== 'cancelled' && o.status !== 'rejected').length;

  if (loading) {
    return (
      <AdminLayout establishmentName={establishment?.name}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
        </div>
      </AdminLayout>
    );
  }

  if (error === 'no_establishment') {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center h-[60vh] text-center">
          <AlertCircle className="w-16 h-16 text-amber-500 mb-4" />
          <h2 className="text-2xl font-bold text-slate-700 mb-2">Nenhum estabelecimento vinculado</h2>
          <p className="text-slate-500">Entre em contato com o administrador do sistema.</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout establishmentName={establishment?.name}>
      {/* Audio for notifications */}
      <audio ref={audioRef} src="https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3" preload="auto" />
      
      {/* Print view */}
      {selectedOrder && (
        <div className="print-only">
          <OrderReceipt order={selectedOrder} establishment={establishment} />
        </div>
      )}
      
      <div className="no-print">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <ShoppingCart className="w-6 h-6" />
              Pedidos
            </h1>
            <p className="text-slate-500">Gerencie os pedidos em tempo real</p>
          </div>
          <div className="flex items-center gap-3">
            {pendingCount > 0 && (
              <Badge variant="destructive" className="animate-pulse px-3 py-1">
                <Bell className="w-4 h-4 mr-1" />
                {pendingCount} pendente(s)
              </Badge>
            )}
            {pixPendingCount > 0 && (
              <Badge className="bg-amber-500 hover:bg-amber-600 px-3 py-1" data-testid="pix-pending-count">
                <QrCode className="w-4 h-4 mr-1" />
                {pixPendingCount} PIX aguardando
              </Badge>
            )}
            {/* WebSocket Connection Indicator */}
            <Badge 
              variant={isConnected ? "outline" : "secondary"}
              className={`px-2 py-1 ${isConnected ? 'text-green-600 border-green-600' : 'text-slate-400'}`}
              title={isConnected ? 'Conectado em tempo real' : 'Reconectando...'}
              data-testid="websocket-status"
            >
              {isConnected ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
            </Badge>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setSoundEnabled(!soundEnabled)}
              title={soundEnabled ? 'Desativar som' : 'Ativar som'}
            >
              {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={fetchOrders}
              title="Atualizar pedidos"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              onClick={() => window.open(`/loja/${establishment?.slug}`, '_blank')}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Ver Loja
            </Button>
          </div>
        </div>

        {/* Kanban Board */}
        <div className="kanban-board" data-testid="kanban-board">
          {['pending', 'confirmed', 'preparing', 'ready'].map(status => (
            <KanbanColumn
              key={status}
              status={status}
              orders={getOrdersByStatus(status)}
              onUpdateStatus={updateOrderStatus}
              onPrint={handlePrintOrder}
              onReject={handleOpenRejectDialog}
              onEditDeliveryFee={handleOpenDeliveryFeeDialog}
              onConfirmPix={handleOpenPixConfirmDialog}
            />
          ))}
        </div>
      </div>

      {/* Reject Order Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent data-testid="reject-order-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Ban className="w-5 h-5" />
              Rejeitar Pedido
            </DialogTitle>
            <DialogDescription>
              Informe o motivo da rejeição do pedido {rejectingOrder?.order_number}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Motivo da Rejeição</Label>
              <Select value={rejectionReason} onValueChange={setRejectionReason}>
                <SelectTrigger data-testid="rejection-reason-select">
                  <SelectValue placeholder="Selecione um motivo" />
                </SelectTrigger>
                <SelectContent>
                  {REJECTION_REASONS.map(reason => (
                    <SelectItem key={reason} value={reason}>{reason}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {rejectionReason === 'Outro motivo' && (
              <div>
                <Label>Descreva o motivo</Label>
                <Textarea
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  placeholder="Digite o motivo..."
                  data-testid="custom-reason-input"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleConfirmReject}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-reject-btn"
            >
              Confirmar Rejeição
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Delivery Fee Dialog */}
      <Dialog open={showDeliveryFeeDialog} onOpenChange={setShowDeliveryFeeDialog}>
        <DialogContent data-testid="delivery-fee-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-500" />
              Ajustar Taxa de Entrega
            </DialogTitle>
            <DialogDescription>
              Ajuste a taxa de entrega do pedido {editingDeliveryFee?.order_number}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-slate-50 p-3 rounded-lg space-y-1">
              <p className="text-sm text-slate-600">
                <strong>Cliente:</strong> {editingDeliveryFee?.customer_name}
              </p>
              <p className="text-sm text-slate-600">
                <strong>Endereço:</strong> {editingDeliveryFee?.customer_address || 'Não informado'}
              </p>
              <p className="text-sm text-slate-600">
                <strong>Subtotal:</strong> {formatCurrency(editingDeliveryFee?.subtotal || 0)}
              </p>
              <p className="text-sm text-slate-600">
                <strong>Taxa atual:</strong> {formatCurrency(editingDeliveryFee?.delivery_fee || 0)}
              </p>
            </div>
            <div>
              <Label htmlFor="delivery-fee">Nova Taxa de Entrega (R$)</Label>
              <Input
                id="delivery-fee"
                type="text"
                value={newDeliveryFee}
                onChange={(e) => setNewDeliveryFee(e.target.value)}
                placeholder="0,00"
                data-testid="delivery-fee-input"
              />
            </div>
            {editingDeliveryFee && (
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm font-medium text-blue-800">
                  Novo Total: {formatCurrency((editingDeliveryFee?.subtotal || 0) + parseFloat(newDeliveryFee.replace(',', '.') || 0))}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeliveryFeeDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleUpdateDeliveryFee}
              className="bg-blue-500 hover:bg-blue-600"
              data-testid="confirm-delivery-fee-btn"
            >
              Atualizar Taxa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm PIX Payment Dialog */}
      <Dialog open={showPixConfirmDialog} onOpenChange={setShowPixConfirmDialog}>
        <DialogContent data-testid="confirm-pix-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <DollarSign className="w-5 h-5" />
              Confirmar Pagamento PIX
            </DialogTitle>
            <DialogDescription>
              Confirme que recebeu o pagamento PIX do pedido {confirmingPixOrder?.order_number}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-green-50 border border-green-200 p-4 rounded-lg space-y-2">
              <p className="text-sm text-green-800">
                <strong>Pedido:</strong> {confirmingPixOrder?.order_number}
              </p>
              <p className="text-sm text-green-800">
                <strong>Cliente:</strong> {confirmingPixOrder?.customer_name}
              </p>
              <p className="text-sm text-green-800">
                <strong>Telefone:</strong> {confirmingPixOrder?.customer_phone}
              </p>
              <p className="text-lg font-bold text-green-900">
                Valor: {formatCurrency(confirmingPixOrder?.total || 0)}
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <p className="font-medium">Antes de confirmar:</p>
              <ul className="list-disc list-inside mt-1 text-amber-700">
                <li>Verifique se o valor recebido está correto</li>
                <li>Confira o comprovante enviado pelo cliente</li>
                <li>Certifique-se de que o pagamento foi creditado</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPixConfirmDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleConfirmPixPayment}
              className="bg-green-500 hover:bg-green-600"
              data-testid="confirm-pix-payment-btn"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Confirmar Pagamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
